import React, { useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  Popover,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CurrencyBitcoinRoundedIcon from '@mui/icons-material/CurrencyBitcoinRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import SportsEsportsRoundedIcon from '@mui/icons-material/SportsEsportsRounded';
import { DesignMode, getDesignTokens } from './designTokens';
import { DesignSurface } from './DesignSurface';
import { SiteButton } from './SiteButton';
import { SiteCountdownTimer } from './SiteCountdownTimer';
import { StatusPill } from './StatusPill';
import { TrustBar } from './TrustBar';

type ShowcaseProps = {
  mode?: DesignMode;
};

const packCards = [
  {
    label: 'One Off Pack',
    title: 'Crystal Vault',
    price: '$49',
    tone: 'warning' as const,
    detail: 'Instant NFT pack with bonus giveaway entries.',
    gradient:
      'radial-gradient(circle at 35% 22%, rgba(243,191,77,0.34), transparent 34%), linear-gradient(135deg, rgba(255,255,255,0.92), rgba(226,237,255,0.88) 58%, rgba(236,231,255,0.86))'
  },
  {
    label: 'Subscription',
    title: 'Nebula Access',
    price: '$19/mo',
    tone: 'info' as const,
    detail: 'Monthly drops, recurring entries, and member rewards.',
    gradient:
      'radial-gradient(circle at 70% 20%, rgba(65,200,196,0.38), transparent 34%), linear-gradient(135deg, rgba(247,247,249,0.94), rgba(222,235,245,0.9) 54%, rgba(229,224,250,0.86))'
  },
  {
    label: 'Premium',
    title: 'Orbit Founder',
    price: '$149',
    tone: 'success' as const,
    detail: 'Higher rarity odds with premium launch benefits.',
    gradient:
      'radial-gradient(circle at 50% 16%, rgba(255,255,255,0.66), transparent 30%), linear-gradient(135deg, rgba(221,246,237,0.94), rgba(217,244,244,0.88))'
  }
];

const navItems = [
  { label: 'Home', icon: <HomeRoundedIcon /> },
  { label: 'Marketplace', icon: <ShoppingBagRoundedIcon /> },
  { label: 'Play', icon: <SportsEsportsRoundedIcon /> },
  { label: 'Rewards', icon: <DiamondRoundedIcon /> }
];

const PackCard = ({
  card,
  mode = 'light',
  compact = false
}: {
  card: (typeof packCards)[number];
  mode?: DesignMode;
  compact?: boolean;
}) => {
  const tokens = getDesignTokens(mode);

  return (
    <DesignSurface
      mode={mode}
      elevated
      sx={{
        p: { xs: 2, sm: 2.4 },
        minHeight: compact ? 430 : 470,
        display: 'grid',
        gridTemplateRows: compact
          ? '132px auto 1fr auto'
          : '150px auto 1fr auto',
        gap: 0
      }}
    >
      <Box
        sx={{
          borderRadius: compact ? 2.5 : 3.5,
          border: `1px solid ${
            mode === 'light' ? 'rgba(29,29,31,0.08)' : tokens.colors.border
          }`,
          background: card.gradient,
          boxShadow:
            mode === 'light'
              ? 'inset 0 1px 0 rgba(255,255,255,0.86), 0 18px 46px rgba(0,0,0,0.08)'
              : 'inset 0 1px 0 rgba(255,255,255,0.16), 0 14px 34px rgba(0,0,0,0.22)'
        }}
      />
      <Box sx={{ mt: 2 }}>
        <StatusPill mode={mode} tone={card.tone}>
          {card.label}
        </StatusPill>
      </Box>
      <Box sx={{ pt: 1.4 }}>
        <Typography
          sx={{
            color: tokens.colors.text,
            fontFamily: 'var(--design-font-display)',
            fontSize: compact ? 22 : 26,
            fontWeight: 900,
            lineHeight: 1.08
          }}
        >
          {card.title}
        </Typography>
        <Typography
          sx={{
            mt: 0.85,
            color: tokens.colors.textMuted,
            lineHeight: 1.55,
            fontSize: compact ? 15 : 16,
            fontWeight: 650
          }}
        >
          {card.detail}
        </Typography>
      </Box>
      <Box
        sx={{
          pt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1
        }}
      >
        <Typography
          sx={{
            color: tokens.colors.text,
            fontSize: compact ? 28 : 34,
            fontWeight: 950
          }}
        >
          {card.price}
        </Typography>
        <SiteButton mode={mode} tone="success" size="small">
          Select
        </SiteButton>
      </Box>
    </DesignSurface>
  );
};

export const TypographyShowcase = ({ mode = 'light' }: ShowcaseProps) => {
  const tokens = getDesignTokens(mode);

  return (
    <DesignSurface mode={mode} elevated sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'grid', gap: 3 }}>
        <Box>
          <StatusPill mode={mode} tone="info">
            Display stack
          </StatusPill>
          <Typography
            sx={{
              mt: 1.5,
              color: tokens.colors.text,
              fontFamily: 'var(--design-font-display)',
              fontSize: { xs: 36, sm: 56 },
              fontWeight: 900,
              lineHeight: 1
            }}
          >
            Crystal Space UI
          </Typography>
        </Box>
        <Box>
          <StatusPill mode={mode} tone="neutral">
            Body stack
          </StatusPill>
          <Typography
            sx={{
              mt: 1.5,
              color: tokens.colors.text,
              fontFamily: 'var(--design-font-body)',
              fontSize: { xs: 18, sm: 21 },
              fontWeight: 700,
              lineHeight: 1.65,
              maxWidth: 820
            }}
          >
            The body stack keeps longer paragraphs readable and calm. Use it for
            forms, cards, helper text, navigation, tables, and any copy that
            needs to be read for more than a moment.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 1.5
          }}
        >
          {[
            ['H1', '64 / 900 / Display'],
            ['Body', '16 / 500 / Body'],
            ['Caption', '12 / 800 / Body']
          ].map(([label, detail]) => (
            <DesignSurface key={label} mode={mode} sx={{ p: 2 }}>
              <Typography sx={{ color: tokens.colors.text, fontWeight: 900 }}>
                {label}
              </Typography>
              <Typography sx={{ mt: 0.5, color: tokens.colors.textMuted }}>
                {detail}
              </Typography>
            </DesignSurface>
          ))}
        </Box>
      </Box>
    </DesignSurface>
  );
};

export const PackCardsShowcase = ({ mode = 'light' }: ShowcaseProps) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        gap: 2
      }}
    >
      {packCards.map((card) => (
        <PackCard key={card.title} card={card} mode={mode} />
      ))}
    </Box>
  );
};

// A stable draw date so the countdown does not reset on every render.
const giveawayDrawDate = new Date(
  Date.now() + 4 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000
);

const giveawayTrustItems = [
  'Fully compliant',
  'Australian owned',
  'Winners announced live',
  'NFTs on every entry',
  'Fair & transparent'
];

const giveawayStats: [string, string][] = [
  ['12,480', "In this week's draw"],
  ['312', 'Draws completed'],
  ['$1.24M', 'Given away in BTC']
];

/**
 * GiveawayShowcase — the signature "giveaway" composition, built entirely from
 * system primitives (SiteButton, SiteCountdownTimer, TrustBar, StatusPill,
 * DesignSurface). High-energy like LMCT+ (big prize, live countdown, green
 * ENTER) while staying clean and light-first, with dark space-glass moments
 * (the prize panel + CTA band) for punch.
 */
export const GiveawayShowcase = ({ mode = 'light' }: ShowcaseProps) => {
  const tokens = getDesignTokens(mode);

  return (
    <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
      {/* Prize hero: light copy on the left, dark space-glass prize on the right */}
      <DesignSurface mode={mode} elevated sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.05fr 0.95fr' },
            gap: { xs: 2.5, lg: 4 },
            alignItems: 'stretch'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Box>
              <StatusPill mode={mode} tone="warning">
                WEEKLY BITCOIN DRAW
              </StatusPill>
            </Box>
            <Typography
              component="h3"
              sx={{
                mt: 1.2,
                color: tokens.colors.text,
                fontFamily: 'var(--design-font-display)',
                fontSize: { xs: 40, sm: 54, md: 60 },
                fontWeight: 900,
                letterSpacing: '-0.04em',
                lineHeight: 0.98
              }}
            >
              Win $10,000 in Bitcoin.
            </Typography>
            <Typography
              sx={{
                mt: 1.6,
                color: tokens.colors.textMuted,
                fontSize: { xs: 16, md: 18 },
                fontWeight: 600,
                lineHeight: 1.5,
                maxWidth: 520
              }}
            >
              Every pack enters you into Australia&apos;s most trusted weekly
              Bitcoin giveaway — with exclusive NFT rewards on every single
              entry.
            </Typography>
            <Box sx={{ mt: 2.4, display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
              <SiteButton
                mode={mode}
                tone="success"
                size="large"
                startIcon={<BoltRoundedIcon />}
              >
                ENTER NOW
              </SiteButton>
              <SiteButton mode={mode} tone="secondary" size="large">
                View winners
              </SiteButton>
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              isolation: 'isolate',
              borderRadius: tokens.radius.lg,
              minHeight: { xs: 240, md: 300 },
              p: { xs: 3, md: 4 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              border: '1px solid rgba(255,255,255,0.14)',
              background:
                'radial-gradient(120% 150% at 15% 0%, #14324a 0%, #0b1020 44%, #05070c 74%, #12212e 100%)',
              boxShadow: '0 24px 60px rgba(5,7,12,0.4)',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                zIndex: -1,
                background:
                  'radial-gradient(200px 150px at 78% 22%, rgba(250,204,21,0.28), transparent 70%), radial-gradient(220px 160px at 20% 82%, rgba(94,234,212,0.22), transparent 70%)',
                filter: 'blur(8px)',
                pointerEvents: 'none'
              }
            }}
          >
            <Box sx={{ color: tokens.colors.gold, '& svg': { fontSize: 40 } }}>
              <CurrencyBitcoinRoundedIcon />
            </Box>
            <Typography
              sx={{
                mt: 1,
                color: 'rgba(248,250,252,0.6)',
                fontSize: 13,
                fontWeight: 850,
                letterSpacing: '0.16em',
                textTransform: 'uppercase'
              }}
            >
              This week&apos;s jackpot
            </Typography>
            <Typography
              sx={{
                mt: 0.6,
                color: tokens.colors.gold,
                fontFamily: 'var(--design-font-display)',
                fontSize: { xs: 60, md: 76 },
                fontWeight: 900,
                letterSpacing: '-0.05em',
                lineHeight: 0.92,
                textShadow: '0 8px 30px rgba(243,191,77,0.28)'
              }}
            >
              $10,000
            </Typography>
            <Typography
              sx={{
                mt: 0.4,
                color: 'rgba(248,250,252,0.72)',
                fontSize: 16,
                fontWeight: 700
              }}
            >
              in Bitcoin
            </Typography>
          </Box>
        </Box>
      </DesignSurface>

      {/* Live countdown */}
      <DesignSurface mode={mode} elevated sx={{ p: { xs: 2, sm: 3 } }}>
        <SiteCountdownTimer
          mode={mode}
          targetDate={giveawayDrawDate}
          size="large"
          label="Next weekly Bitcoin draw closes in"
        />
      </DesignSurface>

      {/* Proof stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: { xs: 1.5, md: 2 }
        }}
      >
        {giveawayStats.map(([value, label]) => (
          <DesignSurface
            key={label}
            mode={mode}
            sx={{ minWidth: 0, p: { xs: 1.5, sm: 2, md: 2.6 } }}
          >
            <Typography
              sx={{
                color: tokens.colors.text,
                fontFamily: 'var(--design-font-display)',
                fontSize: { xs: 24, sm: 32, md: 40 },
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 1
              }}
            >
              {value}
            </Typography>
            <Typography
              sx={{
                mt: 0.7,
                color: tokens.colors.textMuted,
                fontSize: { xs: 10, sm: 12, md: 13 },
                fontWeight: 800,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                lineHeight: 1.3
              }}
            >
              {label}
            </Typography>
          </DesignSurface>
        ))}
      </Box>

      <TrustBar mode={mode} items={giveawayTrustItems} />

      {/* Signature black CTA band */}
      <Box
        sx={{
          borderRadius: tokens.radius.lg,
          px: { xs: 2.5, md: 5 },
          py: { xs: 3, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, md: 4 },
          background:
            'linear-gradient(135deg, #07111f 0%, #0f172a 54%, #052e16 100%)',
          border: '1px solid rgba(34,197,94,0.34)',
          boxShadow: '0 22px 64px rgba(5,7,12,0.3)'
        }}
      >
        <Box>
          <Typography
            sx={{
              color: '#ffffff',
              fontFamily: 'var(--design-font-display)',
              fontSize: { xs: 28, md: 38 },
              fontWeight: 900,
              letterSpacing: '-0.03em',
              lineHeight: 1.05
            }}
          >
            Ready to enter the next draw?
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: 'rgba(255,255,255,0.72)',
              fontSize: { xs: 15, md: 18 },
              fontWeight: 600
            }}
          >
            Choose your pack and secure your entries before the timer ends.
          </Typography>
        </Box>
        <SiteButton
          mode={mode}
          tone="success"
          size="large"
          startIcon={<BoltRoundedIcon />}
          sx={{
            flexShrink: 0,
            minWidth: { xs: '100%', sm: 240 },
            color: '#ffffff',
            background: '#16a34a',
            borderColor: '#16a34a',
            boxShadow: '0 14px 34px rgba(22,163,74,0.36)',
            '&:hover': { background: '#15803d' }
          }}
        >
          ENTER NOW
        </SiteButton>
      </Box>
    </Box>
  );
};

export const CarouselShowcase = ({ mode = 'light' }: ShowcaseProps) => {
  const tokens = getDesignTokens(mode);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const carouselItems = [...packCards, ...packCards];
  const [carouselIndex, setCarouselIndex] = useState(0);

  const scrollCarousel = (direction: 'previous' | 'next') => {
    const carousel = carouselRef.current;
    const firstCard = carousel?.firstElementChild as HTMLElement | null;

    if (!carousel || !firstCard) return;

    const gap = Number.parseFloat(getComputedStyle(carousel).columnGap || '0');
    const distance = firstCard.offsetWidth + gap;
    const nextIndex =
      direction === 'next'
        ? Math.min(carouselItems.length - 1, carouselIndex + 1)
        : Math.max(0, carouselIndex - 1);

    setCarouselIndex(nextIndex);

    carousel.scrollBy({
      left: direction === 'next' ? distance : -distance,
      behavior: 'smooth'
    });
  };

  return (
    <DesignSurface mode={mode} elevated sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          mb: 2
        }}
      >
        <Box>
          <StatusPill mode={mode} tone="success">
            Scroll snap
          </StatusPill>
          <Typography
            sx={{
              mt: 1,
              color: tokens.colors.text,
              fontWeight: 900,
              fontSize: 20
            }}
          >
            Featured pack carousel
          </Typography>
        </Box>
      </Box>
      <Box
        ref={carouselRef}
        onScroll={(event) => {
          const carousel = event.currentTarget;
          const firstCard = carousel.firstElementChild as HTMLElement | null;
          if (!firstCard) return;

          const gap = Number.parseFloat(
            getComputedStyle(carousel).columnGap || '0'
          );
          const itemWidth = firstCard.offsetWidth + gap;
          setCarouselIndex(
            Math.max(
              0,
              Math.min(
                carouselItems.length - 1,
                Math.round(carousel.scrollLeft / itemWidth)
              )
            )
          );
        }}
        sx={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: {
            xs: '88%',
            sm: 'minmax(320px, 46%)',
            lg: 'minmax(340px, 32%)'
          },
          gap: 2,
          overflowX: 'auto',
          px: { xs: '6%', sm: 0 },
          pb: 1,
          scrollSnapType: 'x mandatory',
          scrollPaddingInline: { xs: '6%', sm: 0 },
          scrollbarWidth: 'thin',
          '& > *': {
            scrollSnapAlign: { xs: 'center', sm: 'start' }
          }
        }}
      >
        {carouselItems.map((card, index) => (
          <PackCard
            key={`${card.title}-${index}`}
            card={card}
            mode={mode}
            compact
          />
        ))}
      </Box>
      <Box
        sx={{
          mt: 1.6,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 0.9
        }}
      >
        <Box
          sx={{
            display: { xs: 'inline-flex', sm: 'none' },
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 58,
            height: 42,
            px: 1.2,
            borderRadius: 999,
            color: tokens.colors.text,
            border: `1px solid ${tokens.colors.border}`,
            background:
              mode === 'light'
                ? 'rgba(255,255,255,0.64)'
                : 'rgba(255,255,255,0.06)',
            fontSize: 14,
            fontWeight: 900,
            backdropFilter: 'blur(14px)'
          }}
        >
          {carouselIndex + 1}/{carouselItems.length}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.8 }}>
          {[ArrowBackRoundedIcon, ArrowForwardRoundedIcon].map(
            (Icon, index) => (
              <IconButton
                key={index === 0 ? 'previous' : 'next'}
                aria-label={index === 0 ? 'Previous card' : 'Next card'}
                onClick={() =>
                  scrollCarousel(index === 0 ? 'previous' : 'next')
                }
                sx={{
                  width: 42,
                  height: 42,
                  border: `1px solid ${tokens.colors.border}`,
                  color: tokens.colors.text,
                  background:
                    mode === 'light'
                      ? 'rgba(255,255,255,0.64)'
                      : 'rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(14px)'
                }}
              >
                <Icon />
              </IconButton>
            )
          )}
        </Box>
      </Box>
    </DesignSurface>
  );
};

export const NavigationShowcase = ({ mode = 'light' }: ShowcaseProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const tokens = getDesignTokens(mode);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '0.7fr 1.3fr' },
        gap: 2
      }}
    >
      <DesignSurface mode={mode} elevated sx={{ p: 2 }}>
        <Typography
          sx={{ color: tokens.colors.text, fontWeight: 900, mb: 1.5 }}
        >
          Wide left navigation
        </Typography>
        <List disablePadding sx={{ display: 'grid', gap: 0.8 }}>
          {navItems.map((item, index) => (
            <ListItemButton
              key={item.label}
              selected={index === 0}
              sx={{
                borderRadius: tokens.radius.lg,
                color: tokens.colors.text,
                border: `1px solid ${tokens.colors.border}`,
                '&.Mui-selected': {
                  background:
                    mode === 'light'
                      ? 'rgba(65,200,196,0.14)'
                      : 'rgba(65,200,196,0.12)'
                }
              }}
            >
              {item.icon}
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: 900, ml: 1 }}
              />
            </ListItemButton>
          ))}
        </List>
      </DesignSurface>

      <DesignSurface mode={mode} elevated sx={{ p: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'relative',
            minHeight: 260,
            borderRadius: tokens.radius.lg,
            border: `1px solid ${tokens.colors.border}`,
            background:
              mode === 'light'
                ? 'linear-gradient(135deg, rgba(255,255,255,0.72), rgba(65,200,196,0.1))'
                : 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(65,200,196,0.08))',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              minHeight: { xs: 64, md: 76 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 2, md: 3 },
              py: 0,
              borderBottom: `1px solid ${tokens.colors.border}`
            }}
          >
            <Typography
              sx={{
                color: tokens.colors.text,
                fontFamily: 'var(--design-font-display)',
                fontWeight: 900
              }}
            >
              INCRYPT
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minHeight: { xs: 44, md: 48 },
                  px: { xs: 1, sm: 1.25 },
                  borderRadius: 999,
                  border: `1px solid ${tokens.colors.border}`,
                  background:
                    mode === 'light'
                      ? 'rgba(255,255,255,0.58)'
                      : 'rgba(255,255,255,0.07)',
                  backdropFilter: 'blur(14px)'
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 34, md: 38 },
                    height: { xs: 34, md: 38 },
                    bgcolor: tokens.colors.teal,
                    color: mode === 'light' ? '#062828' : '#041012',
                    fontSize: { xs: 14, md: 15 },
                    fontWeight: 900
                  }}
                >
                  Y
                </Avatar>
                <Typography
                  sx={{
                    color: tokens.colors.text,
                    fontSize: { xs: 15, md: 16 },
                    fontWeight: 900
                  }}
                >
                  yongdev
                </Typography>
              </Box>
              <IconButton
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                onClick={() => setMobileMenuOpen((current) => !current)}
                sx={{
                  width: { xs: 46, md: 50 },
                  height: { xs: 46, md: 50 },
                  color: tokens.colors.text,
                  border: `1px solid ${tokens.colors.border}`,
                  borderRadius: 999,
                  background:
                    mode === 'light'
                      ? 'rgba(255,255,255,0.68)'
                      : 'rgba(255,255,255,0.08)',
                  boxShadow:
                    mode === 'light'
                      ? 'inset 0 1px 0 rgba(255,255,255,0.9), 0 10px 22px rgba(15,23,42,0.08)'
                      : 'inset 0 1px 0 rgba(255,255,255,0.14), 0 10px 22px rgba(0,0,0,0.18)',
                  backdropFilter: 'blur(16px) saturate(160%)',
                  '&:hover': {
                    background:
                      mode === 'light'
                        ? 'rgba(255,255,255,0.82)'
                        : 'rgba(255,255,255,0.12)'
                  },
                  '& svg': {
                    fontSize: { xs: 31, md: 34 }
                  }
                }}
              >
                {mobileMenuOpen ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: { xs: '66%', sm: '50%' },
              pt: { xs: 9, md: 10.5 },
              px: 2,
              pb: 2,
              transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(105%)',
              transition: 'transform 240ms ease',
              borderLeft: `1px solid ${tokens.colors.border}`,
              background:
                mode === 'light'
                  ? 'rgba(255,255,255,0.78)'
                  : 'rgba(12,20,32,0.86)',
              backdropFilter: 'blur(20px) saturate(170%)',
              boxShadow:
                mode === 'light'
                  ? '-18px 0 38px rgba(15,23,42,0.1)'
                  : '-18px 0 38px rgba(0,0,0,0.28)'
            }}
          >
            <Typography
              sx={{
                color: tokens.colors.textMuted,
                fontSize: 15,
                fontWeight: 900
              }}
            >
              Mobile right drawer
            </Typography>
            <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
              {navItems.map((item) => (
                <SiteButton key={item.label} mode={mode} tone="quiet">
                  {item.label}
                </SiteButton>
              ))}
            </Box>
          </Box>
        </Box>
      </DesignSurface>
    </Box>
  );
};

export const FeedbackShowcase = ({ mode = 'light' }: ShowcaseProps) => {
  const tokens = getDesignTokens(mode);

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2
        }}
      >
        {[
          ['success', 'Pack purchased successfully.'],
          ['info', 'Your marketplace listing is now live.'],
          ['warning', 'One payment step still needs attention.'],
          ['error', 'Unable to complete this action.']
        ].map(([severity, message]) => (
          <Alert
            key={severity}
            severity={severity as 'success' | 'info' | 'warning' | 'error'}
            icon={<NotificationsRoundedIcon />}
            sx={{
              minHeight: 76,
              alignItems: 'center',
              borderRadius: tokens.radius.lg,
              color: tokens.colors.text,
              border: `1px solid ${tokens.colors.border}`,
              background:
                mode === 'light'
                  ? 'rgba(255,255,255,0.62)'
                  : 'rgba(255,255,255,0.06)',
              backdropFilter: 'blur(16px)',
              '& .MuiAlert-icon': {
                alignItems: 'center',
                fontSize: 46,
                mr: 1.6,
                py: 0,
                transformOrigin: '50% 8%',
                animation: 'design-bell-ring 1.4s ease-in-out infinite'
              },
              '@keyframes design-bell-ring': {
                '0%, 44%, 100%': { transform: 'rotate(0deg)' },
                '8%': { transform: 'rotate(12deg)' },
                '16%': { transform: 'rotate(-11deg)' },
                '24%': { transform: 'rotate(8deg)' },
                '32%': { transform: 'rotate(-6deg)' }
              },
              '& .MuiAlert-message': {
                py: 0,
                fontSize: { xs: 17, sm: 18 },
                fontWeight: 800,
                lineHeight: 1.35
              }
            }}
          >
            {message}
          </Alert>
        ))}
      </Box>

      <DesignSurface mode={mode} elevated sx={{ p: 2.5 }}>
        <Typography sx={{ color: tokens.colors.text, fontWeight: 900, mb: 1 }}>
          Loading and progress
        </Typography>
        <LinearProgress
          variant="determinate"
          value={68}
          sx={{
            height: 10,
            borderRadius: 999,
            backgroundColor:
              mode === 'light'
                ? 'rgba(15,23,42,0.08)'
                : 'rgba(255,255,255,0.1)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              background: `linear-gradient(90deg, ${tokens.colors.teal}, ${tokens.colors.gold})`
            }
          }}
        />
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 1.2
          }}
        >
          {[1, 2, 3].map((item) => (
            <Skeleton
              key={item}
              variant="rounded"
              height={88}
              sx={{
                borderRadius: tokens.radius.lg,
                bgcolor:
                  mode === 'light'
                    ? 'rgba(15,23,42,0.08)'
                    : 'rgba(255,255,255,0.08)'
              }}
            />
          ))}
        </Box>
      </DesignSurface>
    </Box>
  );
};

export const UtilityPatternsShowcase = ({ mode = 'light' }: ShowcaseProps) => {
  const tokens = getDesignTokens(mode);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const popoverOpen = Boolean(popoverAnchor);

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <DesignSurface mode={mode} elevated sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' },
            gap: 1.5,
            alignItems: 'center'
          }}
        >
          <TextField
            placeholder="Search NFTs, packs, wallet IDs..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: 58,
                borderRadius: 999,
                color: tokens.colors.text,
                fontWeight: 800,
                background:
                  mode === 'light'
                    ? 'rgba(255,255,255,0.66)'
                    : 'rgba(255,255,255,0.06)',
                backdropFilter: 'blur(16px)'
              },
              '& fieldset': {
                borderColor: tokens.colors.border
              }
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <SiteButton
              mode={mode}
              tone="secondary"
              startIcon={<FilterListRoundedIcon />}
            >
              Filters
            </SiteButton>
            <Tooltip title="Use filters to narrow by rarity, status, chain, and price.">
              <IconButton
                aria-label="Filter help"
                sx={{
                  width: 50,
                  height: 50,
                  color: tokens.colors.text,
                  border: `1px solid ${tokens.colors.border}`,
                  background:
                    mode === 'light'
                      ? 'rgba(255,255,255,0.66)'
                      : 'rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(14px)'
                }}
              >
                <HelpOutlineRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DesignSurface>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' },
          gap: 2
        }}
      >
        <DesignSurface mode={mode} sx={{ p: 2.4, textAlign: 'center' }}>
          <Inventory2RoundedIcon
            sx={{ color: tokens.colors.teal, fontSize: 42, mb: 1 }}
          />
          <Typography sx={{ color: tokens.colors.text, fontWeight: 900 }}>
            No NFTs yet
          </Typography>
          <Typography
            sx={{
              mt: 0.6,
              color: tokens.colors.textMuted,
              fontWeight: 650,
              lineHeight: 1.5
            }}
          >
            Purchased packs and claimed rewards will appear here.
          </Typography>
          <SiteButton mode={mode} tone="success" size="small" sx={{ mt: 1.6 }}>
            Browse Packs
          </SiteButton>
        </DesignSurface>

        <DesignSurface mode={mode} sx={{ p: 2.4 }}>
          <Typography
            sx={{ color: tokens.colors.text, fontWeight: 900, mb: 2 }}
          >
            Checkout stepper
          </Typography>
          <Stepper activeStep={1} alternativeLabel>
            {['Cart', 'Payment', 'Confirm'].map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      color: tokens.colors.textMuted,
                      fontWeight: 900
                    },
                    '& .Mui-active, & .Mui-completed': {
                      color: `${tokens.colors.green} !important`
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <Pagination
            count={8}
            page={3}
            size="small"
            siblingCount={0}
            boundaryCount={1}
            sx={{
              mt: 2.4,
              display: 'flex',
              justifyContent: 'center',
              '& .MuiPagination-ul': {
                flexWrap: 'nowrap',
                justifyContent: 'center'
              },
              '& .MuiPaginationItem-root': {
                minWidth: 34,
                height: 34,
                color: tokens.colors.text,
                fontWeight: 900,
                borderColor: tokens.colors.border,
                background:
                  mode === 'light'
                    ? 'rgba(255,255,255,0.52)'
                    : 'rgba(255,255,255,0.06)'
              },
              '& .Mui-selected': {
                background: `${tokens.colors.teal} !important`,
                color: '#041012'
              }
            }}
          />
        </DesignSurface>

        <DesignSurface mode={mode} sx={{ p: 2.4 }}>
          <StatusPill mode={mode} tone="danger">
            Destructive prompt
          </StatusPill>
          <Typography
            sx={{
              mt: 1.2,
              color: tokens.colors.text,
              fontWeight: 900,
              fontSize: 18
            }}
          >
            Remove this listing?
          </Typography>
          <Typography
            sx={{
              mt: 0.7,
              color: tokens.colors.textMuted,
              fontWeight: 650,
              lineHeight: 1.5
            }}
          >
            A compact confirmation pattern before irreversible actions.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <SiteButton mode={mode} tone="quiet" size="small">
              Cancel
            </SiteButton>
            <SiteButton
              mode={mode}
              tone="danger"
              size="small"
              startIcon={<DeleteRoundedIcon />}
            >
              Delete
            </SiteButton>
          </Box>
        </DesignSurface>
      </Box>

      <DesignSurface mode={mode} sx={{ p: 2.4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <SiteButton
            mode={mode}
            tone="secondary"
            onClick={(event) => setPopoverAnchor(event.currentTarget)}
          >
            Open Popover
          </SiteButton>
          <Typography sx={{ color: tokens.colors.textMuted, fontWeight: 750 }}>
            Popovers are useful for richer icon-heavy controls.
          </Typography>
        </Box>
        <Popover
          open={popoverOpen}
          anchorEl={popoverAnchor}
          onClose={() => setPopoverAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: 1,
              maxWidth: 280,
              p: 2,
              borderRadius: tokens.radius.lg,
              color: tokens.colors.text,
              border: `1px solid ${tokens.colors.border}`,
              background:
                mode === 'light'
                  ? 'rgba(255,255,255,0.86)'
                  : 'rgba(12,20,32,0.92)',
              backdropFilter: 'blur(18px) saturate(160%)'
            }
          }}
        >
          <Typography sx={{ fontWeight: 900 }}>Popover title</Typography>
          <Typography
            sx={{ mt: 0.6, color: tokens.colors.textMuted, lineHeight: 1.5 }}
          >
            Put compact controls, explanations, or quick actions in here.
          </Typography>
        </Popover>
      </DesignSurface>
    </Box>
  );
};

export const LayoutRulesShowcase = ({ mode = 'light' }: ShowcaseProps) => {
  const tokens = getDesignTokens(mode);
  const spacingRules = [
    [
      'Page X Padding',
      'xs 16 / sm 24 / md 40',
      'Outer page gutters. Use this for page shells and full-width sections.'
    ],
    [
      'Section Y Gap',
      'xs 48 / md 72',
      'Vertical space between major sections. Keep this generous.'
    ],
    [
      'Header To Content',
      'xs 20 / md 28',
      'Space between a section title block and the first component.'
    ],
    [
      'Component Gap',
      'xs 16 / md 20',
      'Default gap between related component groups.'
    ],
    [
      'Card Gap',
      'xs 16 / md 24',
      'Grid and carousel spacing between product cards or panels.'
    ],
    [
      'Field Gap',
      'xs 12 / md 15',
      'Spacing between form inputs. Keep forms tighter than page sections.'
    ],
    [
      'Inline Gap',
      '10',
      'Small distance between buttons, icons, labels, and compact controls.'
    ]
  ];

  return (
    <DesignSurface mode={mode} elevated sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'grid', gap: tokens.spacing.componentGap }}>
        <Box>
          <Typography
            sx={{
              color: tokens.colors.text,
              fontSize: { xs: 22, md: 26 },
              fontWeight: 900
            }}
          >
            Width Rules
          </Typography>
          <Box
            sx={{
              mt: tokens.spacing.sectionHeaderToContent,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: tokens.spacing.cardGap
            }}
          >
            {[
              [
                'Page shell',
                `${tokens.layout.contentMax}px`,
                'Main pages fill available width until ultra-wide screens.'
              ],
              [
                'Reading width',
                `${tokens.layout.readingMax}px`,
                'Long copy, descriptions, and form helper text stay comfortable.'
              ],
              [
                'Tool width',
                `${tokens.layout.toolMax}px`,
                'Tables, modals, and control-heavy components stay scannable.'
              ]
            ].map(([label, value, detail]) => (
              <DesignSurface key={label} mode={mode} sx={{ p: 2.2 }}>
                <Typography
                  sx={{
                    color: tokens.colors.text,
                    fontFamily: 'var(--design-font-display)',
                    fontSize: 24,
                    fontWeight: 900
                  }}
                >
                  {value}
                </Typography>
                <Typography
                  sx={{ mt: 0.7, color: tokens.colors.text, fontWeight: 900 }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.6,
                    color: tokens.colors.textMuted,
                    fontWeight: 650,
                    lineHeight: 1.55
                  }}
                >
                  {detail}
                </Typography>
              </DesignSurface>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography
            sx={{
              color: tokens.colors.text,
              fontSize: { xs: 22, md: 26 },
              fontWeight: 900
            }}
          >
            Spacing Rules
          </Typography>
          <Box
            sx={{
              mt: tokens.spacing.sectionHeaderToContent,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(4, 1fr)'
              },
              gap: tokens.spacing.cardGap
            }}
          >
            {spacingRules.map(([label, value, detail]) => (
              <DesignSurface key={label} mode={mode} sx={{ p: 2.2 }}>
                <Typography
                  sx={{
                    color: tokens.colors.text,
                    fontFamily: 'var(--design-font-display)',
                    fontSize: 22,
                    fontWeight: 900
                  }}
                >
                  {value}px
                </Typography>
                <Typography
                  sx={{ mt: 0.7, color: tokens.colors.text, fontWeight: 900 }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.6,
                    color: tokens.colors.textMuted,
                    fontWeight: 650,
                    lineHeight: 1.55
                  }}
                >
                  {detail}
                </Typography>
              </DesignSurface>
            ))}
          </Box>
        </Box>
      </Box>
    </DesignSurface>
  );
};
